import React, { useEffect, useRef, useState } from 'react';
import { Create, useDataProvider, useNotify, useRedirect } from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import WizardForm from '../../components/WizardForm';
import { wizardTheme } from '../../components/wizardKit/theme';
import { C, kitCardSx } from '../../components/wizardKit/tokens';
import { getChainId } from '../../lib';
import AudienceStep, { AudienceValue } from './components/AudienceStep';
import LevelsStep, { LevelValue, RecoveryDiscount } from './components/LevelsStep';
import { rewardNeedsPlaces } from './components/LevelRewardEditor';
import SendStep from './components/SendStep';
import BudgetStep from './components/BudgetStep';
import ReviewStep from './components/ReviewStep';
import { SimulationResult } from './components/SimulationPanel';

const NAME_PLACEHOLDER_REGEX = /\{\{\s*nome_do_cliente\s*\}\}/;

const CustomerRecoveryRuleCreate: React.FC<any> = (props) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const chainId = getChainId();
  const [searchParams] = useSearchParams();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [audience, setAudience] = useState<AudienceValue>(() => {
    let prefill: Partial<AudienceValue> = {};
    const raw = searchParams.get('audience');
    if (raw) {
      try { prefill = JSON.parse(raw); } catch { /* ignora prefill inválido */ }
    }
    return {
      segment: prefill.segment || 'at_risk',
      minDaysWithoutFueling: prefill.minDaysWithoutFueling ?? 60,
      maxDaysWithoutFueling: prefill.maxDaysWithoutFueling !== undefined ? prefill.maxDaysWithoutFueling : 240,
      includeNeverFueled: prefill.includeNeverFueled ?? false,
      tagIds: prefill.tagIds ?? [],
      maxCustomers: prefill.maxCustomers ?? null,
    };
  });

  const [levels, setLevels] = useState<LevelValue[]>([
    { levelOrder: 1, discountId: null, reward: null, durationDays: 5, notificationTitle: null, notificationBody: null },
  ]);
  const [recoveryDiscounts, setRecoveryDiscounts] = useState<RecoveryDiscount[]>([]);

  const [sendPush, setSendPush] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [notificationTime, setNotificationTime] = useState<string | null>('15:00');
  const [maxTotalSpend, setMaxTotalSpend] = useState<number | null>(null);
  const [customRate, setCustomRate] = useState(0.15);

  const [completedSteps, setCompletedSteps] = useState<Record<number, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  const [simulation, setSimulation] = useState<SimulationResult | null>(null);
  const [simulationLoading, setSimulationLoading] = useState(false);
  // Erro do simulate aparece na tela; antes caía em "Ajuste os filtros" e escondia a causa.
  const [simulationError, setSimulationError] = useState<string | null>(null);
  const simulationSeq = useRef(0);

  useEffect(() => {
    dataProvider
      .getList('discounts', {
        sort: { field: 'createdAt', order: 'DESC' },
        pagination: { page: 1, perPage: 999 },
        filter: { chainId, type: 'recovery', isEnabled: true },
      })
      .then(({ data }: any) => {
        const list = data.map((d: any) => ({ id: d.id, identify: d.identify, description: d.description }));
        setRecoveryDiscounts(list);
        if (list.length > 0) {
          setLevels((prev) => prev.map((l, i) => (i === 0 && !l.discountId && !l.reward ? { ...l, discountId: list[0].id } : l)));
        }
      })
      .catch(() => {});
  }, [dataProvider, chainId]);

  const buildCampaignInput = (opts: { includeNameDescription: boolean; returnRate?: number }) => {
    const payload: any = {
      chainId,
      audience: {
        minDaysWithoutFueling: audience.minDaysWithoutFueling,
        maxDaysWithoutFueling: audience.maxDaysWithoutFueling,
        includeNeverFueled: audience.includeNeverFueled,
        tagIds: audience.tagIds,
        maxCustomers: audience.maxCustomers,
        segment: audience.segment,
      },
      levels: levels.map((l) => ({
        levelOrder: l.levelOrder,
        discountId: l.reward ? null : l.discountId,
        reward: l.reward,
        durationDays: l.durationDays,
        notificationTitle: l.notificationTitle || null,
        notificationBody: l.notificationBody || null,
      })),
      sendPush,
      sendSms,
      notificationTime: notificationTime || null,
      // Teto zerado ainda sendo digitado: simula sem teto em vez de levar 400 do backend
      // (que exige > 0). Na criação, validateBudget barra antes de chegar aqui.
      maxTotalSpend: maxTotalSpend != null && maxTotalSpend > 0 ? maxTotalSpend : null,
    };
    if (opts.includeNameDescription) {
      payload.name = name;
      payload.description = description || null;
    }
    if (opts.returnRate != null) {
      payload.returnRate = opts.returnRate;
    }
    return payload;
  };

  /** Nível pronto para simular: desconto escolhido, ou brinde com recompensa e local de troca. */
  const levelReady = (l: LevelValue) => {
    if (!l.reward) { return !!l.discountId; }
    const { rewards } = l.reward;
    if (rewards.length === 0) { return false; }
    return rewards.every((r) => {
      // cada recompensa que se troca num balcão precisa dos SEUS locais
      if (rewardNeedsPlaces(r) && r.places.length === 0) { return false; }
      if (r.type === 'existing') return !!r.productId;
      if (r.type === 'fuelDiscount') return !!r.discountId;
      if (r.type === 'drawing_number') return !!r.drawingId;
      return !!r.productName?.trim();
    });
  };

  const levelsValid = levels.length > 0 && levels.every((l) => levelReady(l) && l.durationDays >= 1);
  const audienceRangeValid = audience.maxDaysWithoutFueling == null || audience.maxDaysWithoutFueling > audience.minDaysWithoutFueling;
  const canSimulate = levelsValid && audienceRangeValid;

  const simulationUnavailableReason = !levelsValid
    ? (recoveryDiscounts.length === 0
      ? 'Cadastre um desconto do tipo Recuperação para simular.'
      : 'Complete o nome, desconto e duração de cada nível para simular.')
    : (!audienceRangeValid ? 'Ajuste a faixa de dias sem abastecer.' : null);

  useEffect(() => {
    if (!canSimulate) {
      setSimulation(null);
      setSimulationError(null);
      // Sem isso, um recálculo cancelado aqui deixava o painel em shimmer pra sempre.
      setSimulationLoading(false);
      return;
    }
    const seq = ++simulationSeq.current;
    setSimulationLoading(true);
    const timer = setTimeout(() => {
      dataProvider
        .create('customer-recovery-rules/simulate', { data: buildCampaignInput({ includeNameDescription: false, returnRate: customRate }) })
        .then(({ data }: any) => {
          if (simulationSeq.current === seq) { setSimulation(data); setSimulationError(null); }
        })
        .catch((err: any) => {
          if (simulationSeq.current === seq) {
            setSimulation(null);
            setSimulationError(err?.body?.message || err?.message || 'Não foi possível calcular a contagem.');
          }
        })
        .finally(() => { if (simulationSeq.current === seq) setSimulationLoading(false); });
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(audience), JSON.stringify(levels), sendPush, sendSms, maxTotalSpend, customRate, canSimulate]);

  const validateAudience = () => {
    // O nome mora neste passo: sem isto, quem esquecesse só descobriria na Revisão,
    // cinco passos depois.
    if (!name.trim()) {
      return { error: true, message: 'Dê um nome à campanha.' };
    }
    if (audience.maxDaysWithoutFueling != null && audience.maxDaysWithoutFueling <= audience.minDaysWithoutFueling) {
      return { error: true, message: "O 'até' precisa ser maior que o 'a partir de'." };
    }
    if (audience.maxCustomers != null && audience.maxCustomers > 200000) {
      return { error: true, message: 'Limite de clientes não pode passar de 200.000.' };
    }
    return { error: false };
  };

  const validateLevels = () => {
    const missing = levels.some((l) => !levelReady(l) || !l.durationDays);
    if (missing) {
      return { error: true, message: 'Cada nível precisa de nome, duração e um desconto ou um brinde completo.' };
    }
    const discountIds = levels.map((l) => l.discountId).filter(Boolean);
    if (new Set(discountIds).size !== discountIds.length) {
      return { error: true, message: 'Cada nível precisa de um desconto diferente.' };
    }
    if ((sendPush || sendSms) && !(levels[0].notificationTitle && levels[0].notificationBody)) {
      return { error: true, message: 'O nível 1 precisa de título e mensagem para enviar notificação.' };
    }
    return { error: false };
  };

  const validateSend = () => {
    if (sendSms) {
      const hasPlaceholder = levels.some((l) =>
        NAME_PLACEHOLDER_REGEX.test(l.notificationTitle || '') || NAME_PLACEHOLDER_REGEX.test(l.notificationBody || ''));
      if (hasPlaceholder) {
        return { error: true, message: '{{nome_do_cliente}} só funciona no push; desligue o SMS ou tire o nome da mensagem' };
      }
    }
    return { error: false };
  };

  const validateBudget = () => {
    if (maxTotalSpend != null && !(maxTotalSpend > 0)) {
      return { error: true, message: "Informe o gasto máximo ou marque 'Sem teto de gasto'." };
    }
    return { error: false };
  };

  const hasPlaceholderWithSms = sendSms && levels.some((l) =>
    NAME_PLACEHOLDER_REGEX.test(l.notificationTitle || '') || NAME_PLACEHOLDER_REGEX.test(l.notificationBody || ''));

  const handleCreate = async () => {
    if (!name.trim()) { notify('Nome é obrigatório', { type: 'warning' }); return; }
    const checks = [validateAudience(), validateLevels(), validateSend(), validateBudget()];
    const failed = checks.find((c) => c.error);
    if (failed) { notify(failed.message as string, { type: 'warning' }); return; }

    setSubmitting(true);
    try {
      const { data } = await dataProvider.create('customer-recovery-rules', { data: buildCampaignInput({ includeNameDescription: true }) });
      notify('Campanha criada e iniciada', { type: 'success' });
      redirect('show', 'customer-recovery-rules', data.id);
    } catch (error: any) {
      notify(error.message || 'Erro ao criar campanha', { type: 'warning' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Permission permission={PERMISSIONS.CUSTOMER_RECOVERY_RULES}>
      <Create title="Criar Campanha de Recuperação" {...props} sx={{ '& .RaCreate-card': { ...(kitCardSx as object) } }}>
        <ThemeProvider theme={wizardTheme}>
        {/* Nome e descrição são a faixa de topo do próprio cartão, não um segundo cartão:
            em Paper separado eles ficavam numa largura que não é a do resto da tela. */}
        <WizardForm
          slotProps={{
            root: { mt: 0 },
            form: { p: 0, '&:last-child': { pb: 0 } },
            stepper: { m: 0, px: 3.5, py: 2, borderBottom: `1px solid ${C.lineSoft}` },
            content: { px: 3.5, py: 0 },
          }}
          isLoading={submitting}
          completedSteps={completedSteps}
          handleConcluedStep={(index: number) => setCompletedSteps((v) => ({ ...v, [index]: true }))}
          formSteps={[
            {
              label: 'Público',
              component: (
                <AudienceStep
                  chainId={chainId}
                  name={name}
                  description={description}
                  onNameChange={setName}
                  onDescriptionChange={setDescription}
                  audience={audience}
                  onChange={(patch) => setAudience((prev) => ({ ...prev, ...patch }))}
                  audienceSummary={simulation?.audience ?? null}
                  loading={simulationLoading}
                  unavailableReason={simulationUnavailableReason ?? simulationError}
                />
              ),
              validate: validateAudience,
            },
            {
              label: 'Níveis',
              component: (
                <LevelsStep
                  chainId={chainId}
                  levels={levels}
                  onChange={setLevels}
                  recoveryDiscounts={recoveryDiscounts}
                  audience={{
                    minDaysWithoutFueling: audience.minDaysWithoutFueling,
                    maxDaysWithoutFueling: audience.maxDaysWithoutFueling,
                    segment: audience.segment,
                  }}
                  sendPush={sendPush}
                  sendSms={sendSms}
                  estimatedCost={simulation?.scenarios?.find((sc: any) => sc.returnRate === customRate)?.estimatedCost ?? null}
                  maxTotalSpend={maxTotalSpend}
                />
              ),
              validate: validateLevels,
            },
            {
              label: 'Envio',
              component: (
                <SendStep
                  sendPush={sendPush}
                  sendSms={sendSms}
                  notificationTime={notificationTime}
                  onChange={(patch) => {
                    if (patch.sendPush !== undefined) setSendPush(patch.sendPush);
                    if (patch.sendSms !== undefined) setSendSms(patch.sendSms);
                    if (patch.notificationTime !== undefined) setNotificationTime(patch.notificationTime);
                  }}
                  hasPlaceholderWithSms={hasPlaceholderWithSms}
                />
              ),
              validate: validateSend,
            },
            {
              label: 'Orçamento',
              component: <BudgetStep maxTotalSpend={maxTotalSpend} onChange={setMaxTotalSpend} />,
              validate: validateBudget,
            },
            {
              label: 'Revisão',
              component: (
                <ReviewStep
                  audience={audience}
                  levels={levels}
                  recoveryDiscounts={recoveryDiscounts}
                  sendPush={sendPush}
                  sendSms={sendSms}
                  notificationTime={notificationTime}
                  maxTotalSpend={maxTotalSpend}
                  simulation={simulation}
                  simulationLoading={simulationLoading}
                  simulationUnavailableReason={simulationUnavailableReason}
                  simulationError={simulationError}
                  customRate={customRate}
                  onCustomRateChange={setCustomRate}
                  onConfirm={handleCreate}
                  submitting={submitting}
                />
              ),
              validate: () => ({ error: false }),
              disableToolbar: true,
            },
          ]}
        />
        </ThemeProvider>
      </Create>
    </Permission>
  );
};

export default CustomerRecoveryRuleCreate;
